/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 관심표명자 페이지
 * 버전 : 1.0
 * 파일명 : InterestedPage.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-18, 강동형 : 저작권 추가, 레이아웃 및 디자인 일부 변경
 * 수정 : 2025-12-18, 강동형 : 파일 업로드 기능 추가
 *================================================================
 */

import React, {useEffect, useRef, useState, useCallback} from 'react'
import { Typography, Menu, MenuItem, styled, Button } from '@mui/material'
import InputBoxSearch from 'src/components/inputbox/InputBoxSearch';
import { useInterestedFilterStore, useInterestedStore } from 'src/stores/InterestedStore';
import FilterInterseted from 'src/components/Interested/FilterInterseted';
import { LoadInterestedData } from 'src/components/Interested/useSubmitInterestedFilter';
import InterestedList from 'src/components/Interested/InterestedList';
import InterstedModify from 'src/components/Interested/InterestedModify';
import FilterBarInterested from 'src/components/Interested/FilterBarInterested';
import { Icon_ArrowDown } from 'src/components/common/CommonIcons';
import { useBackHandlerStore } from 'src/stores/UIStore';
import { BottomButtonWrapper } from 'src/components/buttons/CommonButtons';
import UploadFile from 'src/components/Interested/UploadFile';
import { NextButton } from 'src/components/common/CommonStyled';

const FilterBar = styled('div')({
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    backgroundColor:'#f4f4f4',
    padding : '0px 20px',
    paddingTop:'10px',
    marginBottom:'9px'
   });

const SearchContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '10px',

    padding:'10px',
    width:'100%',
    zIndex:2,
    backgroundColor:'white',
    borderBottom:'1px solid #DDDDDD'
  }));
  
const SearchBox = styled('div')(({ theme }) => ({
    zIndex: '10',
    position: 'relative',
    display: 'flex',
    width:'100%',
    height:'46px'
  }));

const FilterButton = styled(Button)({ display: 'flex', alignItems: 'center', marginLeft: 8, color: 'black'});

const ListWarpper = styled('div')({
  marginTop: '-10px',
  minHeight: 'calc(100% - 238px)',
  maxHeight: 'calc(100% - 238px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor:'#f4f4f4'
})

export type InterestPageTap = 'home' | 'modify'

const InterestedPage = () => {
  const interestedData = useInterestedStore((state)=>state.response)
  const reset= useInterestedStore((state)=>state.reset)
  const total_rows= useInterestedStore((state)=>state.total_rows)
  const isCoverdBy= useInterestedStore((state)=>state.isCoverdBy)
  const setField= useInterestedStore((state)=>state.setField)
  const resetFilter = useInterestedFilterStore((state)=>state.reset)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortCriterion, setSortCriterion] = useState('최신순');
  const [render,setRender] = useState<any[]>([])
  const [search, setSearch] = useState('');
  const setFieldFilter = useInterestedFilterStore((state)=>state.setField)
  const [selected, setSelected] = useState<any>('')
  const setCustomAction = useBackHandlerStore((state)=>state.setCustomAction)

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [refReady, setRefReady] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(()=>{
    // 뒤로가기 대신 이 액션을 실행함!
    if(selected !== ''){
      setCustomAction(()=>{
            setSelected('');
            })
        }
    else{
      setCustomAction('')
    }
  },[selected])

  useEffect(()=>{
    handleClose('counsel_date','desc','최신순')
    return()=>{
      reset('response')
      resetFilter()
    }
  },[])
  const didMount = useRef(false);
  useEffect(()=>{
    if (didMount.current) {
      (async () => {
        await LoadInterestedData(false);
      })();
    } else {
      didMount.current = true;
    }
  },[sortCriterion])

  const setLoadMoreRef = useCallback((node: HTMLDivElement | null) => {
    loadMoreRef.current = node;
      // 둘 다 준비됐으면 isRefsReady true로
    if (node && scrollContainerRef.current) {
      setRefReady(true);
    }
    else {
      setRefReady(false); // ❗ ref 해제 시 false로 초기화
    }
  }, []);

  const setLoadScrollRef = useCallback((node: HTMLDivElement | null)=>{
    scrollContainerRef.current = node;
    // 둘 다 준비됐으면 isRefsReady true로
    if (node && loadMoreRef.current) {
      setRefReady(true);
    }
    else {
      setRefReady(false); // ❗ ref 해제 시 false로 초기화
    }
  },[])
        
  useEffect(() => {
    if (!interestedData) return;
    if (!refReady) return;
    const target = loadMoreRef.current;
    const root = scrollContainerRef.current;
    if (!target || !root) {
      return;
    }
        
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        (async () => {
        if (interestedData.length !== 0) {
          await LoadInterestedData(false);
        } else {
          await LoadInterestedData(true);
        }
        })();
      }
    },
    {
      root: null,
      threshold: 0.1,
    }
  );
        
  observer.observe(target);
  observerRef.current = observer;
  return () => {
    observer.disconnect();
    setRefReady(false)
    reset()
    resetFilter()
  };
}, [refReady]);


  useEffect(() => {
    if(interestedData){
      setRender(interestedData)
    }
  }, [interestedData]);

  // 필터 버튼 클릭 이벤트 핸들러
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
  };

  // 필터 메뉴 닫기 이벤트 핸들러
  const handleClose = (sort_by:string, sort_order:string, text:string) => {
    setFieldFilter('sort_by',sort_by)
    setFieldFilter('sort_order',sort_order)
    setSortCriterion(text)
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>)=>{
    setSearch(e.target.value)
    setFieldFilter('student_name',e.target.value)
  }

  const handleSubmit = async () => {
    await LoadInterestedData(false);
  };

  const onClickUpLoadFile = ()=>{
    setField('isCoverdBy','UploadFile')
  }

  return (
    <>
    {!selected ?
    <>
        <SearchContainer>
          <SearchBox>
            <InputBoxSearch placeholder='검색' value={search} onChange={handleSearch} onClick={handleSubmit}/>
          </SearchBox>
          <div style={{width:'100%', overflowX:'auto'}} className='hide-scrollbar'>
            <FilterBarInterested/>
          </div>
        </SearchContainer>
          <FilterBar>
              <Typography variant="body2" sx ={{color:'#7F7F89', fontSize:'12px', fontWeight:500}}>
                  총 <span style={{color:'black',fontSize:'16px',marginLeft:'5px'}}>{total_rows} </span>건
              </Typography>
              <div>
              <FilterButton onClick={handleClick} endIcon={<Icon_ArrowDown style={{fontSize:'14px',color:'#969696'}}/>}>
                  {sortCriterion}
              </FilterButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                  <MenuItem onClick={() => handleClose('counsel_date','desc','최신순')}>최신순</MenuItem>
                  <MenuItem onClick={() => handleClose('counsel_date','asc','과거순')}>과거순</MenuItem>
                  <MenuItem onClick={() => handleClose('student_name','asc','이름순')}>이름순</MenuItem>
                  <MenuItem onClick={() => handleClose('student_name','desc','이름역순')}>이름역순</MenuItem>
                  <MenuItem onClick={() => handleClose('birthday','desc','최근출생순')}>최근출생순</MenuItem>
                  <MenuItem onClick={() => handleClose('birthday','asc','과거출생순')}>과거출생순</MenuItem>
              </Menu>
              </div>
          </FilterBar>
          <ListWarpper ref={setLoadScrollRef}>
              {render?.map((items, index) => (
                  <InterestedList listed={items} key={index} setSelected={setSelected} isLast={render.length-1 === index}/>
              ))}
          <div ref={setLoadMoreRef} style={{ minHeight: '1px' }}/>
          </ListWarpper>
          <BottomButtonWrapper>
            <NextButton onClick={onClickUpLoadFile}>
              관심 표명자 명단 업로드
            </NextButton>
          </BottomButtonWrapper>
      <FilterInterseted/>
    </>:
    <>
      <InterstedModify currentPromotion={selected} setSelected={setSelected} selected={selected}/>
    </>}
    {isCoverdBy &&
      <UploadFile/>
    }
    </>
  )
}

export default InterestedPage